import type { ComponentType, SVGProps } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router';
import { HOME_BY_ROLE, useAuth } from '../auth/AuthProvider';
import { useTranslation } from '../i18n/I18nProvider';
import { cx } from '../lib/cx';
import { Avatar } from '../ui/Avatar';
import { Logo } from './Logo';
import { NotificationBell } from './NotificationBell';
import {
  IconBox,
  IconExternalSite,
  IconCalendar,
  IconCard,
  IconChart,
  IconCheckCircle,
  IconClipboard,
  IconTarget,
  IconDoc,
  IconDumbbell,
  IconBook,
  IconTag,
  IconFlame,
  IconGrid,
  IconHome,
  IconLogout,
  IconMeal,
  IconMessage,
  IconUser,
  IconUsers,
} from './icons';
import { SITE_URL } from '../env';
import type { Role } from '../api/types';

interface NavItem {
  to: string;
  labelKey: string;
  Icon: ComponentType<SVGProps<SVGSVGElement>>;
  end?: boolean;
  /** Prikazuje se u donjoj navigaciji na < 768px. */
  bottom?: boolean;
}

const NAV: Record<Role, NavItem[]> = {
  client: [
    { to: '/', labelKey: 'portal.dashboard', Icon: IconHome, end: true, bottom: true },
    { to: '/trening', labelKey: 'portal.training', Icon: IconDumbbell, bottom: true },
    { to: '/prehrana', labelKey: 'portal.nutrition', Icon: IconMeal, bottom: true },
    { to: '/napredak', labelKey: 'portal.progress', Icon: IconChart, bottom: true },
    { to: '/poruke', labelKey: 'portal.messages', Icon: IconMessage, bottom: true },
    { to: '/edukacija', labelKey: 'shell.education', Icon: IconBook },
    { to: '/navike', labelKey: 'shell.habits', Icon: IconFlame },
    { to: '/check-in', labelKey: 'shell.checkIn', Icon: IconCheckCircle },
    { to: '/kalendar', labelKey: 'shell.calendar', Icon: IconCalendar },
    { to: '/pretplata', labelKey: 'portal.subscription', Icon: IconCard },
    { to: '/profil', labelKey: 'portal.profile', Icon: IconUser },
  ],
  trainer: [
    { to: '/trener', labelKey: 'portal.dashboard', Icon: IconHome, end: true, bottom: true },
    { to: '/trener/klijenti', labelKey: 'shell.clients', Icon: IconUsers, bottom: true },
    { to: '/trener/programi', labelKey: 'shell.programs', Icon: IconGrid, bottom: true },
    { to: '/trener/vjezbe', labelKey: 'shell.exercises', Icon: IconDumbbell },
    { to: '/trener/obroci', labelKey: 'mealTemplates.title', Icon: IconMeal },
    { to: '/trener/mjerenje', labelKey: 'measureGuide.title', Icon: IconTarget },
    { to: '/trener/transformacije', labelKey: 'transformacije.title', Icon: IconTarget },
    { to: '/trener/poruke', labelKey: 'trainer.messages', Icon: IconMessage, bottom: true },
    { to: '/trener/check-in', labelKey: 'trainer.checkIns', Icon: IconClipboard },
    { to: '/kalendar', labelKey: 'shell.calendar', Icon: IconCalendar },
    { to: '/profil', labelKey: 'portal.profile', Icon: IconUser, bottom: true },
  ],
  admin: [
    { to: '/admin', labelKey: 'portal.dashboard', Icon: IconHome, end: true, bottom: true },
    { to: '/admin/korisnici', labelKey: 'shell.users', Icon: IconUsers, bottom: true },
    { to: '/admin/proizvodi', labelKey: 'shell.products', Icon: IconBox, bottom: true },
    { to: '/admin/placanja', labelKey: 'shell.payments', Icon: IconCard, bottom: true },
    { to: '/admin/analitika', labelKey: 'shell.analytics', Icon: IconChart, bottom: true },
    { to: '/admin/kodovi', labelKey: 'shell.discountCodes', Icon: IconTag },
    { to: '/admin/sadrzaj', labelKey: 'shell.content', Icon: IconDoc },
    { to: '/profil', labelKey: 'portal.profile', Icon: IconUser },
  ],
};

/**
 * Sidebar na ≥ 768px, donja navigacija ispod toga.
 * Portal je do faze 1c jedini način da klijent odradi trening na telefonu
 * (ARHITEKTURA.md sekcija 13), pa mobilni raspored nije naknadna dorada.
 */
export function AppShell() {
  const { user, logout } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const role: Role = user?.role ?? 'client';
  const items = NAV[role];
  const bottomItems = items.filter((i) => i.bottom);
  const name = user?.firstName ?? user?.email ?? '';

  return (
    <div className="flex min-h-full flex-col md:flex-row">
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-4 focus:z-50 focus:rounded-pill focus:bg-accent-dark focus:px-4 focus:py-2 focus:text-surface"
      >
        {t('shell.skipToContent')}
      </a>

      {/* Sidebar — desktop */}
      <aside className="sticky top-0 hidden h-dvh w-64 shrink-0 flex-col border-r border-line-subtle bg-surface px-5 py-6 md:flex">
        <div className="mb-8 flex items-center justify-between gap-2">
          <NavLink to={HOME_BY_ROLE[role]}>
            <Logo />
          </NavLink>
          <NotificationBell />
        </div>
        {role !== 'client' && (
          <p className="eyebrow mb-3">{role === 'trainer' ? t('shell.trainer') : t('shell.admin')}</p>
        )}
        <nav className="flex flex-1 flex-col gap-1" aria-label={t('shell.menu')}>
          {items.map(({ to, labelKey, Icon, end }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              className={({ isActive }) =>
                cx(
                  'flex items-center gap-3 rounded-md px-3 py-2.5 text-[14.5px] transition-colors duration-200',
                  isActive
                    ? 'bg-accent-light text-accent-deep'
                    : 'text-ink-muted hover:bg-bg-alt hover:text-ink',
                )
              }
            >
              <Icon />
              {t(labelKey)}
            </NavLink>
          ))}
        </nav>
        <div className="mt-6 border-t border-line-subtle pt-4">
          {/*
            Put natrag na javni sajt. Klijent koji dođe ravno na app adresu
            inače nema odakle doći do cjenika, FAQ-a ni kontakta.
          */}
          <a
            href={SITE_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="mb-3 flex items-center gap-3 rounded-md px-3 py-2 text-[13px] text-ink-muted transition-colors hover:bg-bg-alt hover:text-ink"
          >
            <IconExternalSite width={18} height={18} />
            {t('shell.website')}
          </a>
          <div className="mb-3 flex items-center gap-3">
            <Avatar name={name} size="sm" />
            <span className="min-w-0 truncate text-[13px] text-ink">{name}</span>
          </div>
          <button
            type="button"
            onClick={() => {
              void logout().then(() => navigate('/prijava'));
            }}
            className="flex w-full items-center gap-3 rounded-md px-3 py-2 text-[13px] text-ink-muted transition-colors hover:bg-bg-alt hover:text-ink"
          >
            <IconLogout width={18} height={18} />
            {t('shell.logout')}
          </button>
        </div>
      </aside>

      {/* Gornja traka — mobilni */}
      <header className="sticky top-0 z-30 flex items-center justify-between border-b border-line-subtle bg-bg/90 px-5 py-4 backdrop-blur md:hidden">
        <NavLink to={HOME_BY_ROLE[role]}>
          <Logo compact />
        </NavLink>
        <div className="flex items-center gap-1">
          <a
            href={SITE_URL}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={t('shell.website')}
            title={t('shell.website')}
            className="flex h-10 w-10 items-center justify-center rounded-pill text-ink-muted transition-colors hover:bg-bg-alt hover:text-ink"
          >
            <IconExternalSite width={20} height={20} />
          </a>
          <NotificationBell />
          <NavLink to="/profil" aria-label={t('portal.profile')}>
            <Avatar name={name} size="sm" />
          </NavLink>
        </div>
      </header>

      <main id="main" className="min-w-0 flex-1 pb-24 md:pb-10">
        <div className="mx-auto w-full max-w-content px-5 py-6 md:px-8 md:py-10">
          <Outlet />
        </div>
      </main>

      {/* Donja navigacija — < 768px */}
      <nav
        aria-label={t('shell.menu')}
        className="safe-bottom fixed inset-x-0 bottom-0 z-40 flex border-t border-line-subtle bg-surface/95 backdrop-blur md:hidden"
      >
        {bottomItems.map(({ to, labelKey, Icon, end }) => (
          <NavLink
            key={to}
            to={to}
            end={end}
            className={({ isActive }) =>
              cx(
                'flex flex-1 flex-col items-center gap-1 py-2.5 text-[10px] tracking-[0.3px] transition-colors',
                isActive ? 'text-accent-dark' : 'text-ink-muted',
              )
            }
          >
            <Icon width={20} height={20} />
            <span className="truncate px-1">{t(labelKey)}</span>
          </NavLink>
        ))}
      </nav>
    </div>
  );
}
